import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Save, BookUp } from 'lucide-react';
import InfoHint from '../SharedComponents/InfoHint';
import SettingsSelect from './SettingsSelect';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import { toast } from 'sonner';
import ConfirmModal from '../Common/ConfirmModal';

const KEYS = [
  'semesterar_start_month',
  'semesterskuld_booking_cadence',
  'time_entry_history_retention_years',
];

const DEFAULTS = {
  semesterar_start_month: 4,
  semesterskuld_booking_cadence: 'year_end',
  time_entry_history_retention_years: 7,
};

const MONTH_OPTIONS = [
  { value: 1, label: 'Januari — kalenderår' },
  { value: 2, label: 'Februari' },
  { value: 3, label: 'Mars' },
  { value: 4, label: 'April — Semesterlagens standard' },
  { value: 5, label: 'Maj' },
  { value: 6, label: 'Juni' },
  { value: 7, label: 'Juli — Juli-baserat semesterår' },
  { value: 8, label: 'Augusti' },
  { value: 9, label: 'September' },
  { value: 10, label: 'Oktober' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const CADENCE_OPTIONS = [
  { value: 'year_end', label: 'Bokslut (rekommenderat för K2)' },
  { value: 'monthly', label: 'Månatligen' },
  { value: 'per_payroll', label: 'Per lönekörning' },
  { value: 'manual', label: 'Manuellt' },
];

const PayrollCompanySettings = () => {
  const { companyId } = useParams();
  const [pendingShortRetention, setPendingShortRetention] = useState(null);
  const [retentionAcknowledged, setRetentionAcknowledged] = useState(false);
  const [values, setValues] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.post(
        getApiUrl(`${companyId}/settings/bulk`),
        { keys: KEYS },
        { withCredentials: true },
      );
      const remote = res.data?.settings || {};
      setValues({
        semesterar_start_month: parseInt(remote.semesterar_start_month, 10)
          || DEFAULTS.semesterar_start_month,
        semesterskuld_booking_cadence: remote.semesterskuld_booking_cadence
          || DEFAULTS.semesterskuld_booking_cadence,
        time_entry_history_retention_years: parseInt(
          remote.time_entry_history_retention_years, 10,
        ) || DEFAULTS.time_entry_history_retention_years,
      });
      setDirty(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte ladda inställningar.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const updateField = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const handleSave = async ({ acknowledged = false } = {}) => {
    const years = parseInt(values.time_entry_history_retention_years, 10);
    if (Number.isNaN(years) || years < 1 || years > 20) {
      toast.error('Bevarande-år måste vara 1–20. Bokföringslagen kräver minst 7.');
      return;
    }
    // Sub-legal retention needs an explicit acknowledgement; the modal's
    // confirm re-enters this function with the flag set, so the user never
    // has to press Spara twice.
    if (years < 7 && !retentionAcknowledged && !acknowledged) {
      setPendingShortRetention(years);
      return;
    }
    try {
      setSaving(true);
      await axios.post(
        getApiUrl(`${companyId}/settings/bulk/update`),
        {
          updates: [
            { key: 'semesterar_start_month', value: parseInt(values.semesterar_start_month, 10) },
            { key: 'semesterskuld_booking_cadence', value: values.semesterskuld_booking_cadence },
            { key: 'time_entry_history_retention_years', value: years },
          ],
        },
        { withCredentials: true },
      );
      toast.success('Löneinställningar sparade.');
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Kunde inte spara.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-4">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <>
    <div className="modern-card mt-4">
      <div className="modern-card-header">
        <div>
          <h2 className="section-title">
            <BookUp size={16} />
            Löner &amp; semester
          </h2>
          <p className="section-subtitle">
            Konfiguration för semesterår, semesterskuld och bevarandetid för tidrapporter.
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="m-3">{error}</Alert>
      )}

      <div className="modern-card-body">
        <Form>
          {/* Same field markup as every other settings form: .form-field +
              .modern-label. This page had its own — a per-field icon and a
              <strong> inside a .form-label — so the labels were a different
              size and weight from the rest of Inställningar. */}
          <div className="form-fields">
            <div className="form-field half-width">
              <label htmlFor="semesterarStartMonth" className="modern-label">
                Semesterårets startmånad
                <InfoHint text="Styr vilken månad intjänandeåret börjar. Semesterlagen (1977:480) §3 använder 1 april som standard; många företag följer kalenderår (1 januari) eller juli-baserat år." />
              </label>
              <SettingsSelect
                id="semesterarStartMonth"
                options={MONTH_OPTIONS}
                value={values.semesterar_start_month}
                onChange={(value) => updateField('semesterar_start_month', parseInt(value, 10))}
              />
            </div>

            <div className="form-field half-width">
              <label htmlFor="semesterskuldCadence" className="modern-label">
                Bokföringskadens för semesterskuld (2940)
                <InfoHint text="Vägledning för när justering av semesterlöneskuld ska bokföras. K2-företag bokför typiskt endast vid bokslut; K3 kan kräva månadsvis periodisering." />
              </label>
              <SettingsSelect
                id="semesterskuldCadence"
                options={CADENCE_OPTIONS}
                value={values.semesterskuld_booking_cadence}
                onChange={(value) => updateField('semesterskuld_booking_cadence', value)}
              />
            </div>

            <div className="form-field half-width">
              <label htmlFor="retentionYears" className="modern-label">
                Bevarandetid för tidrapport-historik (år)
                <InfoHint text="Hur länge audit-rader i time_entry_history bevaras. Bokföringslagen (7 kap 2§) kräver minst 7 år. Ett lägre värde kan tillåtas för ej bokföringskopplade rader under GDPR-dataminimering (art. 5.1e)." />
              </label>
              <input
                id="retentionYears"
                type="number"
                className="modern-input"
                min={1}
                max={20}
                value={values.time_entry_history_retention_years}
                onChange={(e) => updateField(
                  'time_entry_history_retention_years',
                  e.target.value === '' ? '' : parseInt(e.target.value, 10),
                )}
              />
            </div>
          </div>

          <div className="d-flex justify-content-end">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!dirty || saving}
              className="d-flex align-items-center gap-2"
            >
              {saving ? <Spinner size="sm" animation="border" /> : <Save size={16} />}
              Spara
            </Button>
          </div>
        </Form>
      </div>
    </div>
      <ConfirmModal
        show={pendingShortRetention !== null}
        title="Kortare bevarandetid än lagen kräver"
        body="Bokföringslagen (7 kap. 2 §) kräver att bokföringsmaterial bevaras i minst 7 år. Sparar du en kortare tid ansvarar du själv för att materialet finns kvar."
        detail={pendingShortRetention !== null ? `Vald bevarandetid: ${pendingShortRetention} år` : null}
        confirmLabel="Spara ändå"
        variant="warning"
        onConfirm={async () => {
          setRetentionAcknowledged(true);
          setPendingShortRetention(null);
          await handleSave({ acknowledged: true });
        }}
        onCancel={() => setPendingShortRetention(null)}
      />
    </>
  );
};

export default PayrollCompanySettings;
